import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, ArrowUpRight, ExternalLink } from "lucide-react";

// Always use current origin — the ingress routes /api/* to the FastAPI backend
// on the same host (works on preview, production, and any custom domain).
const API = typeof window !== "undefined" ? `${window.location.origin}/api` : "/api";

function buildContactUrl(d) {
  const courseParam = `${d.course} (open course spot)`;
  const noteParam = `I'd like to enquire about the ${d.course} on ${d.day} ${d.month} (${d.format}).`;
  return `/contact?course=${encodeURIComponent(courseParam)}&note=${encodeURIComponent(noteParam)}`;
}

export default function UpcomingDates() {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/upcoming-courses`);
        if (!cancelled) setDates(data);
      } catch (err) {
        console.error("Failed to load upcoming courses", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section data-testid="dates-section" id="upcoming-courses" className="section relative scroll-mt-24">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14 items-end">
          <div className="md:col-span-7">
            <p className="eyebrow mb-4">Open Courses · 2026</p>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.02] tracking-tight text-[#1E2A25]">
              Upcoming <span className="italic text-[#4A5D52]">open courses</span>.
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 text-[#4A5D52] leading-relaxed">
            Book individual spots on any of the dates below. Prefer to train your whole team?
            Every course is also available as private <strong className="text-[#1E2A25] font-medium">in-house training</strong>{" "}
            — <Link to="/contact" className="link-underline text-[#1E2A25]">get in touch</Link>.
          </div>
        </div>

        {loading ? (
          <div className="rounded-3xl border border-[#8DA399]/30 bg-[#FDFBF7] p-12 text-center text-[#4A5D52]">
            Loading upcoming courses…
          </div>
        ) : dates.length === 0 ? (
          <div data-testid="no-upcoming" className="rounded-3xl border border-[#8DA399]/30 bg-[#FDFBF7] p-12 text-center">
            <p className="font-serif-display text-2xl text-[#1E2A25]">No dates currently scheduled.</p>
            <p className="mt-3 text-[#4A5D52]">
              New dates are added regularly.{" "}
              <Link to="/contact" className="link-underline text-[#1E2A25]">Get in touch</Link> to be notified.
            </p>
          </div>
        ) : (
          <div className="rounded-3xl border border-[#8DA399]/30 overflow-hidden bg-[#FDFBF7]">
            <div className="hidden md:grid grid-cols-12 gap-6 items-center px-10 py-4 bg-[#F3EFEA] border-b border-[#8DA399]/25 text-xs uppercase tracking-[0.2em] text-[#4A5D52]">
              <div className="col-span-2">Date</div>
              <div className="col-span-3">Course</div>
              <div className="col-span-2">Venue</div>
              <div className="col-span-2">Timings</div>
              <div className="col-span-1">Price</div>
              <div className="col-span-2 text-right">Book</div>
            </div>

            {dates.map((d, i) => (
              <motion.div
                key={d.id || i}
                data-testid={`upcoming-date-${i}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4) }}
                className={`grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-center px-6 md:px-10 py-6 group hover:bg-[#F3EFEA]/70 transition-colors ${
                  i !== dates.length - 1 ? "border-b border-[#8DA399]/20" : ""
                }`}
              >
                <div className="md:col-span-2 flex items-baseline gap-3">
                  <Calendar className="w-4 h-4 text-[#D88C76] flex-shrink-0" />
                  <div>
                    <div className="font-serif-display text-xl leading-none text-[#1E2A25]">{d.day}</div>
                    <div className="text-xs text-[#4A5D52] mt-1 tracking-wider">{d.month}</div>
                  </div>
                </div>
                <div className="md:col-span-3">
                  <div className="text-base text-[#1E2A25] font-medium">{d.course}</div>
                  <div className="md:hidden text-sm text-[#4A5D52] mt-1">{d.format} · {d.timings}</div>
                </div>
                <div className="hidden md:flex md:col-span-2 items-center gap-2 text-sm text-[#4A5D52]">
                  <MapPin className="w-3.5 h-3.5" /> {d.format}
                </div>
                <div className="hidden md:block md:col-span-2 text-sm text-[#4A5D52]">{d.timings}</div>
                <div className="md:col-span-1 text-[#1E2A25] font-medium">{d.price}</div>
                <div className="md:col-span-2 md:text-right">
                  {d.bookingUrl ? (
                    <a
                      href={d.bookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`upcoming-book-${i}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4A5D52] text-[#FDFBF7] text-sm hover:bg-[#3a4c42] transition-all hover:-translate-y-0.5"
                    >
                      Book <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <Link
                      to={buildContactUrl(d)}
                      data-testid={`upcoming-book-${i}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4A5D52] text-[#FDFBF7] text-sm hover:bg-[#3a4c42] transition-all hover:-translate-y-0.5"
                    >
                      Contact us <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
